import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Switch,
  ListItemSecondaryAction,
  useTheme,
} from "@mui/material";
import api from "../../utils/api";

const RolesPage = () => {
  const theme = useTheme();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [assignedPermissionIds, setAssignedPermissionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedRole = roles.find((r) => r.RoleID === selectedRoleId);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get("/roles");
        setRoles(response.data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    const fetchAllPermissions = async () => {
      try {
        const response = await api.get("/permissions");
        setPermissions(response.data);
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };

    fetchRoles();
    fetchAllPermissions();
  }, []);

  useEffect(() => {
    const fetchPermissionsForRole = async () => {
      if (!selectedRoleId || permissions.length === 0) return;
      setLoading(true);
      try {
        const response = await api.get(`/role-permissions/${selectedRoleId}`);
        const permissionIds = response.data?.permissionIds || [];
        setAssignedPermissionIds(permissionIds);
      } catch (error) {
        console.error("Error loading role permissions:", error);
        setAssignedPermissionIds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissionsForRole();
  }, [selectedRoleId, permissions]);

  const handleRoleClick = (roleId) => {
    setSelectedRoleId(roleId);
  };

  const togglePermission = (permissionId) => {
    setAssignedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      await api.post(`/roles/${selectedRoleId}/permissions`, {
        permissionIds: assignedPermissionIds,
      });
      alert("Permissions updated successfully.");
    } catch (err) {
      console.error("Error saving permissions:", err);
      alert("Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 3, p: 4 }}>
      {/* Roles List */}
      <Paper elevation={1} sx={{ width: 260, borderRadius: 2, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Roles
        </Typography>
        <Divider />
        <List dense disablePadding>
          {roles.map((role) => (
            <ListItem
              key={role.RoleID}
              button
              onClick={() => handleRoleClick(role.RoleID)}
              selected={selectedRoleId === role.RoleID}
              sx={{
                borderRadius: 1,
                mt: 1,
                fontWeight: selectedRoleId === role.RoleID ? 600 : 400,
                bgcolor:
                  selectedRoleId === role.RoleID
                    ? theme.palette.grey[300]
                    : "transparent",
                color:
                  selectedRoleId === role.RoleID
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemText primary={role.RoleName} />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Permissions List */}
      <Paper elevation={1} sx={{ flex: 1, borderRadius: 2, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {selectedRole
            ? `Permissions for Role: ${selectedRole.RoleName}`
            : "Select a role to manage permissions"}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : selectedRoleId ? (
          <List dense>
            {permissions.map((perm) => (
              <ListItem
                key={perm.PermissionID}
                sx={{
                  py: 1,
                  borderBottom: "1px solid #eee",
                }}
              >
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: "0.95rem",
                    color: "text.primary",
                  }}
                  primary={perm.Description}
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    size="medium"
                    color="primary"
                    checked={assignedPermissionIds.includes(perm.PermissionID)}
                    onChange={() => togglePermission(perm.PermissionID)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography sx={{ mt: 2, color: "text.secondary" }}>
            Choose a role to view its permission settings.
          </Typography>
        )}

        {/* Save Button */}
        {selectedRoleId && (
          <Box sx={{ textAlign: "right", mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleSavePermissions}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Permissions"}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RolesPage;
